import { useState } from 'react'
import { InfoTooltip, ProductSlug } from 'shared'
import './MFWithdrawFundsModal.scss'
import { WithdrawFundsModalProps } from '../WithdrawFundsModal'
import { WithdrawalFormFields } from '../WithdrawFundsModalGeneric'
import { MFWithdrawForm } from './MFWithdrawForm'
import MFWithdrawReview from './MFWithdrawReview'
import { MFWithdrawCompleted } from './MFWithdrawCompleted'
import { ProductModal } from '../../modal/ProductModal'
import CurrencyTotal from '../../currency/CurrencyTotal'
import getWithdrawApi, {
  getBankAccountDetails,
  WithdrawalTypeEnum,
  WithdrawPostBody,
} from '../../../api/withdraw'

enum WITHDRAW_STEP {
  FORM = 'FORM',
  REVIEW = 'REVIEW',
  COMPLETED = 'COMPLETED',
}

const getWithdrawApiPayload = (values: WithdrawalFormFields) => {
  const hasNewBankAccount =
    !values.useExistingBankAccount &&
    !!values.bankAccountName &&
    !!values.bankAccountNumber
  const data: Partial<WithdrawPostBody> = {
    type: values.withdrawalType,
    useBankAccountOnFile: values.useExistingBankAccount,
    newBankAccount: hasNewBankAccount
      ? {
          name: values.bankAccountName,
          ...getBankAccountDetails(values.bankAccountNumber),
        }
      : null,
  }

  const hasCustomAllocations = values.withdrawalFunds?.length > 0
  if (hasCustomAllocations) {
    // Type: Custom Dollars
    return {
      ...data,
      type: WithdrawalTypeEnum.Partial_Custom_Dollars,
      customAllocations: values.withdrawalFunds.map((f) => ({
        amount: f.amount,
        fundCode: f.assetCode,
      })),
    }
  }

  if (!!values.withdrawalAmount) {
    // Type: Partial
    return {
      ...data,
      totalWithdrawalAmount: values.withdrawalAmount,
    }
  }

  // Type: Full
  return data
}

export const MFWithdrawFundsModal = (props: WithdrawFundsModalProps) => {
  const [currentStep, setCurrentStep] = useState(WITHDRAW_STEP.FORM)
  const [values, setValues] = useState<WithdrawalFormFields>()
  const [isLoading, setIsLoading] = useState(false)

  const { account, authToken, onClose, userId } = props

  const renderStep = () => {
    if (currentStep === WITHDRAW_STEP.FORM) {
      return (
        <MFWithdrawForm
          account={account}
          funds={props.funds}
          onCancel={onClose}
          onSubmit={(values) => {
            setValues(values)
            setCurrentStep(WITHDRAW_STEP.REVIEW)
          }}
          values={values}
          isDisable={isLoading}
        />
      )
    }

    if (currentStep === WITHDRAW_STEP.REVIEW) {
      return (
        <MFWithdrawReview
          account={account}
          values={values}
          onBack={() => setCurrentStep(WITHDRAW_STEP.FORM)}
          isLoading={isLoading}
          onConfirm={() => {
            setIsLoading(true)
            getWithdrawApi(authToken)
              .postWithdraw({
                accountNumber: account.accountNumber,
                fscClientId: userId,
                data: getWithdrawApiPayload(values) as WithdrawPostBody,
              })
              .then(() => {
                setCurrentStep(WITHDRAW_STEP.COMPLETED)
              })
              .finally(() => {
                setIsLoading(false)
              })
          }}
        />
      )
    }

    return (
      <MFWithdrawCompleted
        values={values}
        funds={account.funds}
        onNext={onClose}
      />
    )
  }

  return (
    <ProductModal
      className="managed-funds-withdraw-modal input-tooltip-group"
      shortBackground={currentStep === WITHDRAW_STEP.REVIEW}
      productSlug={ProductSlug.FFMF}
      title="Withdraw funds"
      subtitle={`Managed Funds - ${account.accountNumber}`}
      onClose={onClose}
    >
      {currentStep !== WITHDRAW_STEP.COMPLETED && (
        <InfoTooltip
          className="account-balance-tooltip mb-md"
          tooltipContent={
            <p className="m-0">
              Your current balance is subject to market movement between now and
              the date your withdrawal is processed and may be subject to PIE
              tax. The amount available to be withdrawn may be less than the
              current balance shown.
            </p>
          }
          theme="light"
          direction="right"
        >
          <CurrencyTotal
            className="color-white"
            value={account.accountTotal}
            size="lg"
          />
        </InfoTooltip>
      )}
      {renderStep()}
    </ProductModal>
  )
}
